import type {
  LayoutEngine,
  LayoutInput,
  LayoutResult,
  NodeBounds,
  ConnectorPath,
  BoundingRect,
} from './types';
import type { MindMapNode } from '@/model/types';
import { getStyleForLevel } from '@/theme/defaultTheme';
import { defaultTheme } from '@/theme/defaultTheme';
import { computeNodeExtraHeight } from './nodeExtraHeight';

/** Base radius increment per level. */
const LEVEL_RADIUS = 160;

/**
 * Radial / organic layout: root at center, children radiating outward
 * with each subtree assigned an angular sector proportional to its size.
 */
export const radialLayout: LayoutEngine = {
  compute(input: LayoutInput): LayoutResult {
    const { nodes, rootNodeId, settings, measureText } = input;
    const root = nodes.get(rootNodeId);
    if (!root) {
      return emptyResult();
    }

    const bounds = new Map<string, NodeBounds>();
    const connectors: ConnectorPath[] = [];

    // Measure all visible nodes
    const measured = new Map<string, { width: number; height: number }>();
    for (const [id, node] of nodes) {
      if (node.position) continue;
      const depth = getDepth(node, nodes);
      const style = getStyleForLevel(defaultTheme, depth);
      const textSize = measureText(
        node.text || ' ',
        node.style?.fontSize ?? style.fontSize,
        node.style?.fontWeight ?? style.fontWeight,
      );
      let width = textSize.width + style.paddingX * 2;
      let height = textSize.height + style.paddingY * 2 + computeNodeExtraHeight(node);
      if (node.sizeOverride) {
        width = Math.max(width, node.sizeOverride.width);
        height = Math.max(height, node.sizeOverride.height);
      }
      measured.set(id, { width, height });
    }

    // Count visible descendants (including self) for proportional sector sizing
    const subtreeCount = new Map<string, number>();
    const countDescendants = (nodeId: string): number => {
      const node = nodes.get(nodeId);
      if (!node || node.collapsed) {
        subtreeCount.set(nodeId, 1);
        return 1;
      }
      const visibleKids = node.childIds.filter((id) => nodes.has(id));
      let total = 1;
      for (const childId of visibleKids) {
        total += countDescendants(childId);
      }
      subtreeCount.set(nodeId, total);
      return total;
    };
    countDescendants(rootNodeId);

    // Place root at origin
    const rootSize = measured.get(rootNodeId) ?? { width: 120, height: 40 };
    bounds.set(rootNodeId, {
      id: rootNodeId,
      x: -rootSize.width / 2,
      y: -rootSize.height / 2,
      width: rootSize.width,
      height: rootSize.height,
      centerX: 0,
      centerY: 0,
      depth: 0,
    });

    // Position children radially
    const positionRadial = (
      parentId: string,
      childIds: string[],
      depth: number,
      startAngle: number,
      endAngle: number,
    ) => {
      if (childIds.length === 0) return;

      const totalWeight = childIds.reduce(
        (sum, id) => sum + (subtreeCount.get(id) ?? 1),
        0,
      );
      const angleSpan = endAngle - startAngle;
      let currentAngle = startAngle;

      const parentBounds = bounds.get(parentId);
      if (!parentBounds) return;

      for (const childId of childIds) {
        const childNode = nodes.get(childId);
        if (!childNode) continue;

        const weight = subtreeCount.get(childId) ?? 1;
        const childAngleSpan = (weight / totalWeight) * angleSpan;
        const midAngle = currentAngle + childAngleSpan / 2;

        const radius = LEVEL_RADIUS * depth + settings.horizontalGap * (depth - 1);
        const cx = radius * Math.cos(midAngle);
        const cy = radius * Math.sin(midAngle);
        const childSize = measured.get(childId) ?? { width: 100, height: 30 };

        const childBounds: NodeBounds = {
          id: childId,
          x: cx - childSize.width / 2,
          y: cy - childSize.height / 2,
          width: childSize.width,
          height: childSize.height,
          centerX: cx,
          centerY: cy,
          depth,
        };
        bounds.set(childId, childBounds);

        // Curved connector from parent center to child center
        connectors.push({
          parentId,
          childId,
          path: curvedRadialPath(
            { x: parentBounds.centerX, y: parentBounds.centerY },
            { x: cx, y: cy },
          ),
        });

        // Recurse
        if (!childNode.collapsed) {
          const grandchildren = childNode.childIds.filter((id) => nodes.has(id));
          positionRadial(
            childId,
            grandchildren,
            depth + 1,
            currentAngle,
            currentAngle + childAngleSpan,
          );
        }

        currentAngle += childAngleSpan;
      }
    };

    const visibleChildren = root.collapsed
      ? []
      : root.childIds.filter((id) => nodes.has(id));
    positionRadial(rootNodeId, visibleChildren, 1, 0, Math.PI * 2);

    // Apply manual position overrides and handle disconnected nodes
    let hasOverrides = false;
    for (const [id, node] of nodes) {
      if (!node.position) continue;
      if (hasCollapsedAncestor(node, nodes)) {
        bounds.delete(id);
        continue;
      }
      hasOverrides = true;
      const size = measured.get(id) ?? { width: 100, height: 30 };
      const existing = bounds.get(id);
      bounds.set(id, {
        id,
        x: node.position.x,
        y: node.position.y,
        width: size.width,
        height: size.height,
        centerX: node.position.x + size.width / 2,
        centerY: node.position.y + size.height / 2,
        depth: existing?.depth ?? 0,
      });
    }

    // Rebuild connectors from final bounds if any positions were overridden
    if (hasOverrides) {
      connectors.length = 0;
      for (const [id, node] of nodes) {
        if (!node.parentId) continue;
        const childB = bounds.get(id);
        const parentB = bounds.get(node.parentId);
        if (!childB || !parentB) continue;
        connectors.push({
          parentId: node.parentId,
          childId: id,
          path: curvedRadialPath(
            { x: parentB.centerX, y: parentB.centerY },
            { x: childB.centerX, y: childB.centerY },
          ),
        });
      }
    }

    const totalBounds = computeTotalBounds(bounds);
    return { nodeBounds: bounds, connectorPaths: connectors, totalBounds };
  },
};

/** Generate a curved path from parent to child in radial layout. */
function curvedRadialPath(
  from: { x: number; y: number },
  to: { x: number; y: number },
): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;

  // Control point offset perpendicular to the line for a gentle curve
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const curveFactor = 0.15;
  const cpX = midX - dy * curveFactor;
  const cpY = midY + dx * curveFactor;

  return `M ${from.x} ${from.y} Q ${cpX} ${cpY}, ${to.x} ${to.y}`;
}

function getDepth(node: MindMapNode, nodes: Map<string, MindMapNode>): number {
  let depth = 0;
  let current = node;
  while (current.parentId) {
    depth++;
    const parent = nodes.get(current.parentId);
    if (!parent) break;
    current = parent;
  }
  return depth;
}

function hasCollapsedAncestor(node: MindMapNode, nodes: Map<string, MindMapNode>): boolean {
  let current = node;
  while (current.parentId) {
    const parent = nodes.get(current.parentId);
    if (!parent) return false;
    if (parent.collapsed) return true;
    current = parent;
  }
  return false;
}

function computeTotalBounds(bounds: Map<string, NodeBounds>): BoundingRect {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const b of bounds.values()) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }

  if (!isFinite(minX)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function emptyResult(): LayoutResult {
  return { nodeBounds: new Map(), connectorPaths: [], totalBounds: { x: 0, y: 0, width: 0, height: 0 } };
}
